import { useEffect, useRef } from 'react';
import { Box, Text } from '@mantine/core';
import { notifications as toastNotifications } from '@mantine/notifications';
import { BellSimpleIcon } from '@phosphor-icons/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getNotificationsControllerGetNotificationsQueryKey,
  getNotificationsControllerGetNotificationsQueryOptions,
} from '@/generated/api/notifications.ts';
import type {
  GetNotificationsResponseDto,
  NotificationDto,
  NotificationDtoSeverity,
} from '@/generated/api/pacApi.schemas.ts';
import type {
  SseEvent,
  SseStatus,
} from '@/common/notifications/cross-tab-sse-store.ts';
import {
  getBrowserNotificationPermission,
  requestBrowserNotificationPermission,
  showBrowserNotification as showNativeBrowserNotification,
} from '@/common/notifications/browser-notifications.ts';
import { useProductAnalytics } from '@/common/analytics/product-analytics.tsx';
import { PRODUCT_ANALYTICS_EVENT } from '@/common/analytics/events.ts';

export type NotificationSignal = {
  id: string;
  subject: string;
  body: string;
  severity?: NotificationDtoSeverity | string;
};

type NotificationCoordinatorResult = {
  notifications: NotificationDto[];
  totalCount: number;
  unreadCount: number;
  isLoading: boolean;
  isError: boolean;
  refetch: () => Promise<void>;
};

type NotificationCoordinatorProps = {
  events: SseEvent[];
  status?: SseStatus;
  tabIsLeader?: boolean;
  fallbackPollIntervalMs?: number;
  activeUserThresholdMs?: number;
};

const DEFAULT_FALLBACK_POLL_INTERVAL_MS = 30000;
const DEFAULT_ACTIVE_USER_THRESHOLD_MS = 120000;

const notificationQueryKey =
  getNotificationsControllerGetNotificationsQueryKey();

const severityColor: Record<NotificationDtoSeverity, string> = {
  info: 'blue',
  success: 'green',
  warning: 'yellow',
  error: 'red',
};

export function useNotificationCoordinator({
  events,
  status,
  tabIsLeader = false,
  fallbackPollIntervalMs = DEFAULT_FALLBACK_POLL_INTERVAL_MS,
  activeUserThresholdMs = DEFAULT_ACTIVE_USER_THRESHOLD_MS,
}: NotificationCoordinatorProps): NotificationCoordinatorResult {
  const capture = useProductAnalytics();
  const audioRef = useRef(new Audio('/sounds/chime.mp3'));
  const queryClient = useQueryClient();
  const hydratedRef = useRef(false);
  const knownNotificationIdsRef = useRef(new Set<string>());
  const processedSignalIdsRef = useRef(new Set<string>());
  const lastSeenEventIdsRef = useRef(
    new Set(events.map((event) => event.id)),
  );
  const lastInteractionAtRef = useRef(0);

  const pollingEnabled = isRealtimeDegraded(status);
  const notificationsQuery = useQuery(
    getNotificationsControllerGetNotificationsQueryOptions(undefined, {
      query: {
        refetchInterval: pollingEnabled ? fallbackPollIntervalMs : false,
        refetchIntervalInBackground: true,
      },
    }),
  );

  useEffect(() => {
    const updateInteractionTime = () => {
      lastInteractionAtRef.current = Date.now();
    };

    updateInteractionTime();

    window.addEventListener('focus', updateInteractionTime);
    window.addEventListener('keydown', updateInteractionTime);
    window.addEventListener('pointerdown', updateInteractionTime);
    window.addEventListener('scroll', updateInteractionTime, true);

    return () => {
      window.removeEventListener('focus', updateInteractionTime);
      window.removeEventListener('keydown', updateInteractionTime);
      window.removeEventListener('pointerdown', updateInteractionTime);
      window.removeEventListener('scroll', updateInteractionTime, true);
    };
  }, []);

  useEffect(() => {
    if (getBrowserNotificationPermission() !== 'default') return;

    void requestBrowserNotificationPermission();
  }, []);

  useEffect(() => {
    for (const event of [...events].reverse()) {
      if (lastSeenEventIdsRef.current.has(event.id)) continue;
      lastSeenEventIdsRef.current.add(event.id);

      if (event.event === 'notifications.snapshot') {
        queryClient.setQueryData<GetNotificationsResponseDto>(
          notificationQueryKey,
          (current) =>
            current
              ? { ...current, unreadCount: event.data.unread_count }
              : current,
        );
        continue;
      }

      if (event.event !== 'notification.in_app.created') continue;

      knownNotificationIdsRef.current.add(event.data.notification_recipient_id);

      if (tabIsLeader) {
        capture(PRODUCT_ANALYTICS_EVENT.notificationReceived, {
          delivery: 'realtime',
          severity: normalizeSeverity(event.data.severity),
          has_actions: event.data.actions.length > 0,
        });
      }

      processNewNotification(
        {
          id: event.data.notification_recipient_id,
          subject: event.data.subject,
          body: event.data.body_markdown || event.data.subtitle || '',
          severity: event.data.severity,
        },
        {
          tabIsLeader,
          activeUserThresholdMs,
          lastInteractionAt: lastInteractionAtRef.current,
          processedSignalIds: processedSignalIdsRef.current,
          allowBrowserNotification: tabIsLeader,
        },
        audioRef.current,
      );

      void queryClient.invalidateQueries({ queryKey: notificationQueryKey });
    }
  }, [activeUserThresholdMs, capture, events, queryClient, tabIsLeader]);

  useEffect(() => {
    const notifications = notificationsQuery.data?.notifications;
    if (!notifications) return;

    if (!hydratedRef.current) {
      for (const notification of notifications) {
        knownNotificationIdsRef.current.add(notification.recipientId);
      }
      hydratedRef.current = true;
      return;
    }

    for (const notification of [...notifications].reverse()) {
      if (knownNotificationIdsRef.current.has(notification.recipientId)) {
        continue;
      }

      knownNotificationIdsRef.current.add(notification.recipientId);

      if (notification.readAt !== null) continue;

      if (tabIsLeader) {
        capture(PRODUCT_ANALYTICS_EVENT.notificationReceived, {
          delivery: 'poll',
          severity: notification.severity ?? 'info',
          has_actions: (notification.actions?.length ?? 0) > 0,
        });
      }

      processNewNotification(notificationToSignal(notification), {
        tabIsLeader: false,
        activeUserThresholdMs,
        lastInteractionAt: lastInteractionAtRef.current,
        processedSignalIds: processedSignalIdsRef.current,
        allowBrowserNotification: true,
      });
    }
  }, [
    activeUserThresholdMs,
    capture,
    notificationsQuery.data?.notifications,
    tabIsLeader,
  ]);

  useDocumentUnreadTitle(notificationsQuery.data?.unreadCount ?? 0);

  return {
    notifications: notificationsQuery.data?.notifications ?? [],
    totalCount: notificationsQuery.data?.totalCount ?? 0,
    unreadCount: notificationsQuery.data?.unreadCount ?? 0,
    isLoading: notificationsQuery.isLoading,
    isError: notificationsQuery.isError,
    refetch: async () => {
      await notificationsQuery.refetch();
    },
  };
}

function normalizeSeverity(value: string) {
  return ['info', 'success', 'warning', 'error'].includes(value)
    ? value
    : 'unknown';
}

export function isRealtimeDegraded(status?: SseStatus) {
  return status !== 'connected' && status !== 'listening';
}

export function isActiveNotificationSession({
  activeUserThresholdMs,
  lastInteractionAt,
  now = Date.now(),
}: {
  activeUserThresholdMs: number;
  lastInteractionAt: number;
  now?: number;
}) {
  return (
    document.visibilityState === 'visible' &&
    document.hasFocus() &&
    now - lastInteractionAt <= activeUserThresholdMs
  );
}

export function formatUnreadTitle(baseTitle: string, unreadCount: number) {
  const cleanBaseTitle = stripUnreadTitlePrefix(baseTitle);
  return unreadCount > 0
    ? `(${unreadCount}) ${cleanBaseTitle}`
    : cleanBaseTitle;
}

export function stripUnreadTitlePrefix(title: string) {
  return title.replace(/^\(\d+\)\s+/, '');
}

function useDocumentUnreadTitle(unreadCount: number) {
  const baseTitleRef = useRef<string | null>(null);

  useEffect(() => {
    if (baseTitleRef.current === null) {
      baseTitleRef.current = stripUnreadTitlePrefix(document.title);
    }

    document.title = formatUnreadTitle(baseTitleRef.current, unreadCount);

    return () => {
      if (baseTitleRef.current !== null) {
        document.title = baseTitleRef.current;
      }
    };
  }, [unreadCount]);
}

function processNewNotification(
  signal: NotificationSignal,
  {
    tabIsLeader,
    activeUserThresholdMs,
    lastInteractionAt,
    processedSignalIds,
    allowBrowserNotification,
  }: {
    tabIsLeader: boolean;
    activeUserThresholdMs: number;
    lastInteractionAt: number;
    processedSignalIds: Set<string>;
    allowBrowserNotification: boolean;
  },
  audioRef?: HTMLAudioElement,
) {
  if (processedSignalIds.has(signal.id)) return;
  processedSignalIds.add(signal.id);

  // Notification chime
  if (tabIsLeader && audioRef) {
    try {
      audioRef.currentTime = 0;
      audioRef.play();
    } catch (error) {
      console.error('failed to play notification chime', error);
    }
  }

  if (
    isActiveNotificationSession({
      activeUserThresholdMs,
      lastInteractionAt,
    })
  ) {
    showNotificationToast(signal);
    return;
  }

  if (allowBrowserNotification) {
    showBrowserNotification(signal);
  }
}

function notificationToSignal(
  notification: NotificationDto,
): NotificationSignal {
  return {
    id: notification.recipientId,
    subject: notification.subject,
    body: notification.body,
    severity: notification.severity,
  };
}

function showNotificationToast(signal: NotificationSignal) {
  const color = getSeverityColor(signal.severity);

  toastNotifications.show({
    id: `notification:${signal.id}`,
    title: signal.subject,
    message: (
      <Box>
        <Text size="sm" lineClamp={2}>
          {signal.body || 'New notification'}
        </Text>
      </Box>
    ),
    color,
    icon: <BellSimpleIcon size={18} weight="fill" />,
    position: 'top-right',
    autoClose: 8000,
    withBorder: true,
  });
}

function showBrowserNotification(signal: NotificationSignal) {
  showNativeBrowserNotification(signal.subject, {
    body: signal.body,
  });
}

function getSeverityColor(severity: NotificationSignal['severity']) {
  if (
    severity === 'info' ||
    severity === 'success' ||
    severity === 'warning' ||
    severity === 'error'
  ) {
    return severityColor[severity];
  }

  return severityColor.info;
}
